import { useEffect, useRef, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface UseInstallPromptReturn {
  canInstall: boolean;
  install: () => Promise<"accepted" | "dismissed" | null>;
}

/**
 * Captures the browser's `beforeinstallprompt` event so the app can present
 * its own install UI at the right moment.
 *
 * - `canInstall`: true when the event has been captured and not yet consumed.
 * - `install()`:  triggers the native prompt; returns the user's choice or
 *                 null if no prompt is available.
 */
export function useInstallPrompt(): UseInstallPromptReturn {
  const [canInstall, setCanInstall] = useState(false);
  const promptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      promptRef.current = event as BeforeInstallPromptEvent;
      setCanInstall(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    };
  }, []);

  const install = async (): Promise<"accepted" | "dismissed" | null> => {
    const prompt = promptRef.current;
    if (!prompt) return null;

    promptRef.current = null;
    setCanInstall(false);

    const { outcome } = await prompt.prompt();
    return outcome;
  };

  return { canInstall, install };
}
